<script setup>
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAvatarColor } from 'dashboard/composables/useAvatarColor';

const props = defineProps({
  activeTabs: { type: Array, default: () => [] },
  tabCounts: {
    type: Object,
    default: () => ({
      today: null,
      overdue: null,
      upcoming: null,
      no_date: null,
    }),
  },
});

const emit = defineEmits([
  'dateSelected',
  'meeting-open',
  'new-meeting',
  'tab-change',
]);

const store = useStore();

const { t } = useI18n();

const QUICK_TABS = computed(() => [
  { key: 'today', label: t('TASKS.TABS.TODAY') },
  { key: 'overdue', label: t('TASKS.TABS.OVERDUE') },
  { key: 'upcoming', label: t('TASKS.TABS.UPCOMING') },
  { key: 'no_date', label: t('TASKS.TABS.NO_DATE') },
]);

// ── Calendário ──────────────────────────────────────────────
const today = new Date();
today.setHours(0, 0, 0, 0);

const currentYear = ref(today.getFullYear());
const currentMonth = ref(today.getMonth());
const rangeStart = ref(null); // Date | null — primeiro clique
const rangeEnd = ref(null); // Date | null — ⌘+clique

const WEEKDAY_LABELS = ['D', 'S', 'T', 'Q', 'Q', 'S', 'S'];

const MONTH_NAMES = [
  'Janeiro',
  'Fevereiro',
  'Março',
  'Abril',
  'Maio',
  'Junho',
  'Julho',
  'Agosto',
  'Setembro',
  'Outubro',
  'Novembro',
  'Dezembro',
];

const monthLabel = computed(
  () => `${MONTH_NAMES[currentMonth.value]} ${currentYear.value}`
);

function prevMonth() {
  if (currentMonth.value === 0) {
    currentMonth.value = 11;
    currentYear.value -= 1;
  } else {
    currentMonth.value -= 1;
  }
}

function nextMonth() {
  if (currentMonth.value === 11) {
    currentMonth.value = 0;
    currentYear.value += 1;
  } else {
    currentMonth.value += 1;
  }
}

function prevYear() {
  currentYear.value -= 1;
}

function nextYear() {
  currentYear.value += 1;
}

const calendarDays = computed(() => {
  const firstDay = new Date(currentYear.value, currentMonth.value, 1).getDay();
  const daysInMonth = new Date(
    currentYear.value,
    currentMonth.value + 1,
    0
  ).getDate();
  const blanks = Array.from({ length: firstDay }, () => null);
  const days = Array.from({ length: daysInMonth }, (_, i) => i + 1);
  return [...blanks, ...days];
});

// ── Tasks do store ───────────────────────────────────────────
const allTasks = computed(() => store.getters['tasks/allTasks'] ?? []);

const daysWithTasks = computed(() => {
  const set = new Set();
  allTasks.value.forEach(task => {
    if (!task.due_date) return;
    const d = new Date(task.due_date * 1000);
    if (
      d.getFullYear() === currentYear.value &&
      d.getMonth() === currentMonth.value
    ) {
      set.add(d.getDate());
    }
  });
  return set;
});

function isToday(day) {
  return (
    day === today.getDate() &&
    currentMonth.value === today.getMonth() &&
    currentYear.value === today.getFullYear()
  );
}

function isRangeStart(day) {
  if (!rangeStart.value || !day) return false;
  return (
    rangeStart.value.getDate() === day &&
    rangeStart.value.getMonth() === currentMonth.value &&
    rangeStart.value.getFullYear() === currentYear.value
  );
}

function isRangeEnd(day) {
  if (!rangeEnd.value || !day) return false;
  return (
    rangeEnd.value.getDate() === day &&
    rangeEnd.value.getMonth() === currentMonth.value &&
    rangeEnd.value.getFullYear() === currentYear.value
  );
}

function isInRange(day) {
  if (!rangeStart.value || !rangeEnd.value || !day) return false;
  const date = new Date(currentYear.value, currentMonth.value, day);
  return date > rangeStart.value && date < rangeEnd.value;
}

function emitSingleDay(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  emit('dateSelected', {
    from: `${y}-${m}-${d}T00:00:00`,
    to: `${y}-${m}-${d}T23:59:59`,
  });
}

function emitRange() {
  const s = rangeStart.value;
  const e = rangeEnd.value;
  const fmt = (date, time) => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}T${time}`;
  };
  emit('dateSelected', {
    from: fmt(s, '00:00:00'),
    to: fmt(e, '23:59:59'),
  });
}

function selectDay(day, event) {
  if (!day) return;
  const clicked = new Date(currentYear.value, currentMonth.value, day);

  if (event?.metaKey && rangeStart.value) {
    // ⌘+clique: define fim do intervalo
    if (clicked.getTime() === rangeStart.value.getTime()) {
      // mesmo dia do início → trata como clique simples
      rangeEnd.value = null;
      emitSingleDay(clicked);
      return;
    }
    // garante que start < end
    if (clicked < rangeStart.value) {
      rangeEnd.value = rangeStart.value;
      rangeStart.value = clicked;
    } else {
      rangeEnd.value = clicked;
    }
    emitRange();
    return;
  }

  // clique simples — segundo clique no rangeStart desmarca (com ou sem rangeEnd)
  if (rangeStart.value && clicked.getTime() === rangeStart.value.getTime()) {
    rangeStart.value = null;
    rangeEnd.value = null;
    emit('dateSelected', null);
    return;
  }

  rangeStart.value = clicked;
  rangeEnd.value = null;
  emitSingleDay(clicked);
}

function clearSelection() {
  rangeStart.value = null;
  rangeEnd.value = null;
}

defineExpose({ clearSelection });

// ── Status de reunião ────────────────────────────────────────
const MEETING_STATUS_OPTIONS = [
  {
    value: 'scheduled',
    label: 'Agendada',
    color: 'text-n-blue-11 bg-n-blue-2 border-n-blue-4 hover:bg-n-blue-3',
    dateColor: 'bg-n-blue-2 text-n-blue-11',
  },
  {
    value: 'done',
    label: 'Realizada',
    color: 'text-n-teal-11 bg-n-teal-2 border-n-teal-4 hover:bg-n-teal-3',
    dateColor: 'bg-n-teal-2 text-n-teal-11',
  },
  {
    value: 'no_show',
    label: 'No-show',
    color: 'text-n-ruby-11 bg-n-ruby-2 border-n-ruby-4 hover:bg-n-ruby-3',
    dateColor: 'bg-n-ruby-2 text-n-ruby-11',
  },
  {
    value: 'rescheduled',
    label: 'Reagendada',
    color: 'text-n-amber-11 bg-n-amber-2 border-n-amber-4 hover:bg-n-amber-3',
    dateColor: 'bg-n-amber-2 text-n-amber-11',
  },
  {
    value: 'cancelled',
    label: 'Cancelada',
    color: 'text-n-slate-10 bg-n-alpha-1 border-n-weak hover:bg-n-alpha-2',
    dateColor: 'bg-n-surface-2 text-n-slate-10',
  },
];

function meetingStatusOption(val) {
  return (
    MEETING_STATUS_OPTIONS.find(o => o.value === val) ||
    MEETING_STATUS_OPTIONS[0]
  );
}

// ── Assignee ─────────────────────────────────────────────────
const agents = useMapGetter('agents/getAgents');

function meetingWatchers(meeting) {
  const ids = meeting.watcher_ids || [];
  return (agents.value ?? []).filter(a => ids.includes(a.id));
}

function meetingContactLabel(meeting) {
  return meeting.contact?.company_name || meeting.contact?.name || '';
}

const { avatarColor, initials: agentInitials } = useAvatarColor();
function initials(name) {
  return agentInitials(name ?? '') || '?';
}

// ── Próximas reuniões ────────────────────────────────────────
const allMeetings = computed(() => store.getters['agenda/allMeetings'] ?? []);

onMounted(() => {
  store.dispatch('agenda/fetchMeetings', {});
});

const upcomingMeetings = computed(() => {
  const meetings = allMeetings.value.filter(meeting => {
    if (!meeting.start_at) return false;
    if (['done', 'no_show', 'cancelled'].includes(meeting.status)) return false;
    const d = new Date(meeting.start_at);

    if (rangeStart.value && rangeEnd.value) {
      const from = new Date(rangeStart.value);
      from.setHours(0, 0, 0, 0);
      const to = new Date(rangeEnd.value);
      to.setHours(23, 59, 59, 999);
      return d >= from && d <= to;
    }

    if (rangeStart.value && !rangeEnd.value) {
      return (
        d.getFullYear() === rangeStart.value.getFullYear() &&
        d.getMonth() === rangeStart.value.getMonth() &&
        d.getDate() === rangeStart.value.getDate()
      );
    }

    return d >= today;
  });

  meetings.sort((a, b) => new Date(a.start_at) - new Date(b.start_at));

  if (!rangeStart.value && !rangeEnd.value) {
    return meetings.slice(0, 5);
  }
  return meetings;
});

function formatMeetingTime(meeting) {
  const startDate = new Date(meeting.start_at);
  const endDate = meeting.end_at
    ? new Date(meeting.end_at)
    : new Date(startDate.getTime() + 3600000);
  const fmt = { hour: '2-digit', minute: '2-digit' };
  return `${startDate.toLocaleTimeString('pt-BR', fmt)} – ${endDate.toLocaleTimeString('pt-BR', fmt)}`;
}

function formatMeetingMonth(meeting) {
  return new Date(meeting.start_at)
    .toLocaleDateString('pt-BR', { month: 'short' })
    .replace('.', '')
    .toUpperCase();
}

function formatMeetingDay(meeting) {
  return new Date(meeting.start_at).getDate();
}
</script>

<template>
  <aside
    class="w-full md:w-72 xl:w-96 flex-shrink-0 md:border-l border-n-weak bg-n-background flex flex-col overflow-y-auto h-full"
  >
    <!-- Filtros rápidos -->
    <div class="px-3 pt-3 pb-2 border-b border-n-weak flex-shrink-0">
      <div class="flex items-center gap-0.5">
        <button
          v-for="tab in QUICK_TABS"
          :key="tab.key"
          class="flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-medium transition-colors whitespace-nowrap"
          :class="
            props.activeTabs.includes(tab.key)
              ? 'bg-n-alpha-2 text-n-brand'
              : 'text-n-slate-10 hover:bg-n-alpha-1 hover:text-n-slate-12'
          "
          @click="emit('tab-change', tab.key)"
        >
          {{ tab.label }}
          <span
            v-if="
              props.tabCounts[tab.key] !== null &&
              props.tabCounts[tab.key] !== undefined
            "
            class="text-[10px] font-semibold px-1 py-px rounded-full min-w-[16px] text-center"
            :class="
              props.activeTabs.includes(tab.key)
                ? 'bg-n-alpha-2 text-n-brand'
                : 'bg-n-surface-2 text-n-slate-10'
            "
          >
            {{ props.tabCounts[tab.key] }}
          </span>
        </button>
      </div>
    </div>

    <!-- Calendário -->
    <div class="px-5 pt-2 pb-5">
      <!-- Header do mês -->
      <div class="flex items-center justify-between mb-2">
        <div class="flex items-center">
          <button
            class="p-1 rounded hover:bg-n-alpha-1 text-n-slate-11"
            @click="prevYear"
          >
            <span class="i-lucide-chevrons-left size-4" />
          </button>
          <button
            class="p-1 rounded hover:bg-n-alpha-1 text-n-slate-11"
            @click="prevMonth"
          >
            <span class="i-lucide-chevron-left size-4" />
          </button>
        </div>
        <span class="text-base font-semibold text-n-slate-12">{{
          monthLabel
        }}</span>
        <div class="flex items-center">
          <button
            class="p-1 rounded hover:bg-n-alpha-1 text-n-slate-11"
            @click="nextMonth"
          >
            <span class="i-lucide-chevron-right size-4" />
          </button>
          <button
            class="p-1 rounded hover:bg-n-alpha-1 text-n-slate-11"
            @click="nextYear"
          >
            <span class="i-lucide-chevrons-right size-4" />
          </button>
        </div>
      </div>

      <!-- Labels dos dias da semana -->
      <div class="grid grid-cols-7 mb-2">
        <span
          v-for="label in WEEKDAY_LABELS"
          :key="label"
          class="text-center text-xs text-n-slate-10 font-medium py-1"
        >
          {{ label }}
        </span>
      </div>

      <!-- Grid de dias -->
      <div class="grid grid-cols-7 gap-y-1">
        <div
          v-for="(day, idx) in calendarDays"
          :key="idx"
          class="flex flex-col items-center py-0.5"
        >
          <template v-if="day">
            <button
              class="relative w-9 h-9 flex items-center justify-center rounded-md text-sm transition-colors"
              :class="[
                isRangeStart(day) || isRangeEnd(day)
                  ? 'bg-n-brand text-white font-semibold'
                  : isInRange(day)
                    ? isToday(day)
                      ? 'bg-n-alpha-2 text-n-brand font-bold'
                      : 'bg-n-alpha-2 text-n-brand'
                    : isToday(day)
                      ? 'text-n-brand font-bold hover:bg-n-alpha-1'
                      : 'text-n-slate-12 hover:bg-n-alpha-1',
              ]"
              @click="selectDay(day, $event)"
            >
              {{ day }}
            </button>
            <span
              v-if="daysWithTasks.has(day)"
              class="w-1 h-1 rounded-full mt-0.5"
              :class="
                isRangeStart(day) || isRangeEnd(day)
                  ? 'bg-white/70'
                  : 'bg-n-brand'
              "
            />
            <span v-else class="w-1 h-1 mt-0.5" />
          </template>
        </div>
      </div>
    </div>

    <!-- Divisor -->
    <div class="border-t border-n-weak mx-4" />

    <!-- Seção: Próximas Reuniões -->
    <div class="p-4 flex flex-col gap-2">
      <div class="flex items-center justify-between">
        <h3
          class="text-xs font-semibold text-n-slate-10 uppercase tracking-wider flex items-center gap-1.5"
        >
          <span class="i-lucide-calendar size-3.5" />
          {{ $t('TASKS.UPCOMING_MEETINGS') }}
        </h3>
        <button
          class="flex items-center justify-center size-6 bg-n-brand text-white rounded-lg hover:brightness-110 transition-all overflow-visible"
          :title="$t('TASKS.NEW_MEETING')"
          @click="emit('new-meeting')"
        >
          <span class="i-lucide-plus size-2.5 flex-shrink-0" />
        </button>
      </div>

      <div v-if="upcomingMeetings.length > 0" class="flex flex-col gap-2">
        <div
          v-for="meeting in upcomingMeetings"
          :key="meeting.id"
          class="flex gap-2.5 p-2.5 rounded-lg bg-n-surface-2 hover:bg-n-alpha-1 cursor-pointer"
          @click="emit('meeting-open', meeting.id)"
        >
          <!-- Bloco mês/dia -->
          <div
            class="flex flex-col items-center justify-center rounded-md px-2 py-1 min-w-[38px] flex-shrink-0 self-center transition-colors"
            :class="
              meetingStatusOption(meeting.status || 'scheduled').dateColor
            "
          >
            <span class="text-[10px] font-semibold uppercase leading-none">
              {{ formatMeetingMonth(meeting) }}
            </span>
            <span class="text-lg font-bold leading-tight">
              {{ formatMeetingDay(meeting) }}
            </span>
          </div>

          <!-- Conteúdo: título + linha empresa/horário/status à esquerda, avatar/link à direita -->
          <div class="flex-1 min-w-0 flex items-center justify-between gap-2">
            <!-- Texto: título colado com a linha de baixo -->
            <div class="min-w-0 flex flex-col gap-1">
              <p
                class="text-sm font-semibold text-n-slate-12 truncate leading-none m-0"
              >
                {{ meeting.title }}
              </p>

              <!-- Linha 2: horário · empresa/contato -->
              <div class="flex items-center gap-1.5 min-w-0 leading-none">
                <span
                  class="text-[11px] text-n-slate-10 flex-shrink-0 leading-none"
                >
                  {{ formatMeetingTime(meeting) }}
                </span>
                <a
                  v-if="
                    !meetingContactLabel(meeting) && meeting.google_meet_url
                  "
                  :href="meeting.google_meet_url"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="flex-shrink-0 text-n-slate-10 hover:text-n-brand transition-colors"
                  :title="$t('TASKS.OPEN_GOOGLE_MEET')"
                  @click.stop
                >
                  <span class="i-lucide-arrow-up-right size-3" />
                </a>
                <span
                  v-if="meetingContactLabel(meeting)"
                  class="text-[11px] text-n-slate-10 truncate leading-none"
                >
                  {{ meetingContactLabel(meeting) }}
                </span>
                <a
                  v-if="meetingContactLabel(meeting) && meeting.google_meet_url"
                  :href="meeting.google_meet_url"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="flex-shrink-0 text-n-slate-10 hover:text-n-brand transition-colors"
                  :title="$t('TASKS.OPEN_GOOGLE_MEET')"
                  @click.stop
                >
                  <span class="i-lucide-arrow-up-right size-3" />
                </a>
              </div>
              <!-- /linha 2 -->
            </div>
            <!-- /texto -->

            <!-- Avatar / link Google Calendar -->
            <div class="flex flex-row items-center flex-shrink-0">
              <!-- Seguidores (empilhados, mais recente à direita) -->
              <div
                v-if="meetingWatchers(meeting).length"
                class="flex items-center"
              >
                <div
                  v-for="(watcher, idx) in [
                    ...meetingWatchers(meeting),
                  ].reverse()"
                  :key="watcher.id"
                  class="size-7 rounded-full flex-shrink-0 flex items-center justify-center text-[8px] font-bold text-white overflow-hidden"
                  :class="idx > 0 ? '-ml-1.5' : ''"
                  :style="{
                    zIndex: idx,
                    ...(watcher.thumbnail
                      ? {
                          backgroundImage: `url(${watcher.thumbnail})`,
                          backgroundSize: 'cover',
                          backgroundPosition: 'center',
                        }
                      : { backgroundColor: avatarColor(watcher) }),
                  }"
                  :title="watcher.name"
                >
                  <span v-if="!watcher.thumbnail">{{
                    initials(watcher.name)
                  }}</span>
                </div>
              </div>

              <!-- Avatar / responsável (sempre à direita, por cima) -->
              <div
                class="relative"
                :class="meetingWatchers(meeting).length ? '-ml-1.5' : ''"
                :style="{ zIndex: meetingWatchers(meeting).length }"
              >
                <div
                  class="size-7 rounded-full flex items-center justify-center flex-shrink-0"
                  :style="
                    meeting.user?.thumbnail
                      ? {
                          backgroundImage: `url(${meeting.user.thumbnail})`,
                          backgroundSize: 'cover',
                          backgroundPosition: 'center',
                        }
                      : meeting.user
                        ? { backgroundColor: avatarColor(meeting.user) }
                        : {}
                  "
                  :class="
                    !meeting.user ? 'bg-n-surface-2 border border-n-weak' : ''
                  "
                  :title="meeting.user?.name"
                >
                  <span
                    v-if="!meeting.user?.thumbnail && meeting.user"
                    class="text-[11px] font-bold text-white"
                    >{{ initials(meeting.user.name) }}</span
                  >
                  <span
                    v-else-if="!meeting.user"
                    class="i-lucide-user-plus size-3.5 text-n-slate-9 flex-shrink-0"
                  />
                </div>
              </div>
            </div>
            <!-- /avatar / link -->
          </div>
          <!-- /conteúdo -->
        </div>
      </div>

      <div
        v-else
        class="flex flex-col items-center justify-center py-6 gap-2 text-center"
      >
        <span class="i-lucide-calendar-x size-8 text-n-slate-9" />
        <p class="text-xs text-n-slate-10">{{ $t('TASKS.NO_MEETINGS') }}</p>
      </div>
    </div>
  </aside>
</template>
